import { useCallback, useEffect, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import type { PlaceDto } from '@/api/types/Place';
import { copyText } from '@/utils/clipboard';
import { formatPlaceSms } from '@/utils/placeNote';

// „Kopírovat adresu" behaviour, shared verbatim by the desktop table and the
// mobile card tree (CHR-06 §6 / prototype `#s-copy`):
//   clipboard ← the WHOLE row as SMS-ready multi-line text (`formatPlaceSms`,
//   user request 2026-09-17), toast „Adresa zkopírována: <název>" (the copied
//   text is multi-line and would not fit a snackbar), and the triggering
//   control shows a check for 2 s.
// A hook rather than props threaded from the page, so neither tree can drift
// from the other on format, wording or timing.

export const COPIED_FEEDBACK_MS = 2000;

export interface PlaceCopy {
  // Id of the place copied within the last `COPIED_FEEDBACK_MS`, or `null`.
  copiedId: string | null;
  copyPlace: (place: PlaceDto) => Promise<void>;
}

export const usePlaceCopy = (): PlaceCopy => {
  const { t } = useTranslation('places');
  const { enqueueSnackbar } = useSnackbar();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A pending timer must not fire into an unmounted tree (the page swaps trees
  // on a viewport change while the check is still showing).
  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    },
    [],
  );

  const copyPlace = useCallback(
    async (place: PlaceDto): Promise<void> => {
      const text = formatPlaceSms(place);
      const ok = await copyText(text);
      if (!ok) {
        enqueueSnackbar(t('toast.copyFailed'), { variant: 'error' });
        return;
      }
      enqueueSnackbar(t('toast.copied', { name: place.name }), {
        variant: 'success',
      });
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      setCopiedId(place.id);
      timerRef.current = setTimeout(() => setCopiedId(null), COPIED_FEEDBACK_MS);
    },
    [enqueueSnackbar, t],
  );

  return { copiedId, copyPlace };
};
